import {Actions, Context} from "vuex-smart-module";
import {AxiosError, AxiosResponse} from "axios";
import {Store} from "vuex";
import {store} from "@/store";
import root from "@/store/modules";
import esquireClient from "@/modules/esquire-client";
import QrServerState, {QrServerStatus} from "@/store/modules/qr-server/qr-server-state";
import QrServerGetters from "@/store/modules/qr-server/qr-server-getters";
import QrServerMutations from "@/store/modules/qr-server/qr-server-mutations";
import QrServerCodeData, {IQrServerCodeApiData} from "@/http/data/qr-server-code-data";
import QrServerLogPageData, {IQrServerLogPageApiData} from "@/http/data/qr-server-log-page-data";
import QrServerLogFiltersData from "@/http/data/qr-server-log-filters-data";
import QrServerBuildingRequest from "@/http/requests/qr-server-building-request";

interface QrServerErrorResponse {
    code: string;
    message: string;
    errors: Record<string, unknown>;
}

export default class QrServerActions extends Actions<
    QrServerState,
    QrServerGetters,
    QrServerMutations,
    QrServerActions
> {
    private rootContext!: Context<typeof root>;

    $init(_store: Store<typeof store>): void {
        this.rootContext = root.context(_store);

        super.$init(_store);
    }

    private async notifyUnhandledError(response: AxiosResponse): Promise<void> {
        const errorMessage = this.resolveErrorMessage(response);

        await this.rootContext.actions.message({
            message: errorMessage,
            type: "error",
            duration: 0,
            showClose: true
        });
    }

    private resolveErrorMessage(response: AxiosResponse): string {
        if (response.status === 400) {
            const errorResponse = response.data as QrServerErrorResponse;

            return errorResponse.message;
        }

        return `${response.status} ${response.statusText}`;
    }

    async initialize(): Promise<void> {
        this.mutations.setIsInitialized(true);
    }

    async rehydrateStore(): Promise<void> {
        return;
    }

    async updateLogFilters(filters: QrServerLogFiltersData): Promise<void> {
        this.mutations.setLogFilters(filters);
    }

    async indexCodes(buildingId: string): Promise<void> {
        this.mutations.setCodesStatus(QrServerStatus.Loading);

        const request = new QrServerBuildingRequest(buildingId);
        const response = await esquireClient
            .get("qr-server/codes", {
                params: request.toJson()
            })
            .catch((e: AxiosError) => e.response);

        if (!response) return;

        if (response.status === 200) {
            const codes = response.data.map((data: IQrServerCodeApiData) => new QrServerCodeData(data));
            const status = codes.length > 0 ? QrServerStatus.Ready : QrServerStatus.Empty;

            this.mutations.setCodes(codes);
            this.mutations.setCodesStatus(status);
            return;
        }

        if (response.status === 400) {
            const errorResponse = response.data as QrServerErrorResponse;

            if (errorResponse.code === "not_implemented") {
                this.mutations.setCodes([]);
                this.mutations.setCodesStatus(QrServerStatus.NotImplemented);
                return;
            }
        }

        if (response.status === 401) {
            this.mutations.setCodes([]);
            this.mutations.setCodesStatus(QrServerStatus.Unauthenticated);
            return;
        }

        if (response.status === 403) {
            this.mutations.setCodes([]);
            this.mutations.setCodesStatus(QrServerStatus.Forbidden);
            return;
        }

        this.mutations.setCodes([]);
        this.mutations.setCodesStatus(QrServerStatus.Failed);
        await this.notifyUnhandledError(response);
    }

    async indexLogs(payload: { buildingId: string; page: number; perPage: number }): Promise<void> {
        const {buildingId, page, perPage} = payload;
        this.mutations.setLogsStatus(QrServerStatus.Loading);

        const filters = this.state.logFilters.clone();
        this.mutations.setLogFilters(filters);
        const response = await esquireClient
            .get("qr-server/logs", {
                params: filters.toJson(buildingId, page, perPage)
            })
            .catch((e: AxiosError) => e.response);

        if (!response) return;

        if (response.status === 200) {
            const logsPage = new QrServerLogPageData(response.data as IQrServerLogPageApiData);
            const status = logsPage.data.length > 0 ? QrServerStatus.Ready : QrServerStatus.Empty;

            this.mutations.setLogs(logsPage.data);
            this.mutations.setLogFilterOptions(logsPage.filters);
            this.mutations.setLogsMeta(logsPage.meta);
            this.mutations.setSelectedLogsPage(page);
            this.mutations.setLastFetchedLogFilters(filters);
            this.mutations.setLogsStatus(status);
            return;
        }

        if (response.status === 400) {
            const errorResponse = response.data as QrServerErrorResponse;

            if (errorResponse.code === "not_implemented") {
                this.mutations.setLogs([]);
                this.mutations.setLogsStatus(QrServerStatus.NotImplemented);
                this.mutations.setLogsMeta(null);
                return;
            }
        }

        if (response.status === 401) {
            this.mutations.setLogs([]);
            this.mutations.setLogsStatus(QrServerStatus.Unauthenticated);
            this.mutations.setLogsMeta(null);
            return;
        }

        if (response.status === 403) {
            this.mutations.setLogs([]);
            this.mutations.setLogsStatus(QrServerStatus.Forbidden);
            this.mutations.setLogsMeta(null);
            return;
        }

        this.mutations.setLogs([]);
        this.mutations.setLogsStatus(QrServerStatus.Failed);
        this.mutations.setLogsMeta(null);
        await this.notifyUnhandledError(response);
    }
}
